import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
  RouterProvider,
} from '@tanstack/react-router'
import { Cog } from '~/components/brand'
import { HomePage } from '~/routes/home'
import { NotFoundPage } from '~/routes/not-found'
import { AuthGate } from './auth-gate'

/**
 * Auth gating lives in one component at the root rather than in per-route guards.
 * There are only three gates — signed out, first-run setup, and "must replace the
 * temporary password" — and each replaces the entire screen, so a wrapper reads
 * more clearly than repeating a `beforeLoad` on every route.
 */
const rootRoute = createRootRoute({
  component: () => (
    <AuthGate>
      <Outlet />
    </AuthGate>
  ),
  notFoundComponent: NotFoundPage,
})

/** Shown while a route chunk is in flight. Deliberately quiet — most are instant. */
function RoutePending() {
  return (
    <div className="grid min-h-64 place-items-center" aria-busy>
      <Cog className="size-6 text-primary" />
    </div>
  )
}

// Generic over the literal path so the route tree keeps its exact paths and
// `<Link to="/activity">` is type-checked rather than silently widened to `string`.
const route = <TPath extends string>(path: TPath, component: () => React.JSX.Element) =>
  createRoute({ getParentRoute: () => rootRoute, path, component })

/**
 * Every screen except Home is a separate chunk.
 *
 * This is what keeps the first load inside the budget with twenty-odd screens in
 * the app: a buyer never downloads the tier editor, and an admin never downloads
 * the basket. `scripts/check-bundle-size.mjs` measures the entry chunk and its
 * static imports, so splitting actually counts.
 */
const lazyRoute = <TPath extends string>(
  path: TPath,
  load: () => Promise<Record<string, unknown>>,
  exportName: string,
) =>
  createRoute({
    getParentRoute: () => rootRoute,
    path,
    // biome-ignore lint/suspicious/noExplicitAny: lazyRouteComponent's importer type is intentionally loose
    component: lazyRouteComponent(load as any, exportName),
    pendingComponent: RoutePending,
  })

/**
 * The one route that reads a search parameter. `?start=<chapter>` is how a nudge on
 * an empty screen, a notification, or somebody's bookmark jumps straight into a
 * part of the walkthrough instead of the top of the list.
 */
const guideRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/guide',
  validateSearch: (search: Record<string, unknown>): { start?: string } =>
    typeof search.start === 'string' ? { start: search.start } : {},
  component: lazyRouteComponent(() => import('~/routes/guide'), 'GuidePage'),
  pendingComponent: RoutePending,
})

const routeTree = rootRoute.addChildren([
  route('/', HomePage),
  guideRoute,

  // People and oversight
  lazyRoute('/businesses', () => import('~/routes/businesses'), 'BusinessesPage'),
  lazyRoute('/people', () => import('~/routes/people'), 'PeoplePage'),
  lazyRoute('/activity', () => import('~/routes/activity'), 'ActivityPage'),
  lazyRoute('/settings', () => import('~/routes/settings'), 'SettingsPage'),
  lazyRoute('/reports', () => import('~/routes/reports'), 'ReportsPage'),
  lazyRoute('/money', () => import('~/routes/money'), 'MoneyPage'),
  lazyRoute('/notifications', () => import('~/routes/notifications'), 'NotificationsPage'),

  // Catalog master
  lazyRoute('/catalog', () => import('~/routes/catalog/products'), 'ProductsPage'),
  lazyRoute('/catalog/categories', () => import('~/routes/catalog/categories'), 'CategoriesPage'),
  lazyRoute(
    '/catalog/suggestions',
    () => import('~/routes/catalog/suggestions'),
    'SuggestionsPage',
  ),
  lazyRoute('/pricing', () => import('~/routes/catalog/pricing'), 'PricingPage'),
  lazyRoute('/price-books', () => import('~/routes/catalog/price-books'), 'PriceBooksPage'),
  lazyRoute('/stock', () => import('~/routes/stock'), 'StockPage'),

  // Seller
  lazyRoute('/items', () => import('~/routes/seller/items'), 'SellerItemsPage'),
  lazyRoute('/price-checks', () => import('~/routes/seller/price-checks'), 'PriceChecksPage'),
  lazyRoute('/supply-orders', () => import('~/routes/seller/supply-orders'), 'SupplyOrdersPage'),

  // Buyer
  lazyRoute('/shop', () => import('~/routes/shop/browse'), 'BrowsePage'),
  lazyRoute('/shop/$productId', () => import('~/routes/shop/product'), 'ProductDetailPage'),
  lazyRoute('/cart', () => import('~/routes/shop/cart'), 'CartPage'),

  // Deals, shared by both sides of the desk
  lazyRoute('/requests', () => import('~/routes/deals/requests'), 'RequestsPage'),
  lazyRoute('/requests/$id', () => import('~/routes/deals/request-detail'), 'RequestDetailPage'),
  lazyRoute('/orders', () => import('~/routes/deals/orders'), 'OrdersPage'),
  lazyRoute('/orders/$id', () => import('~/routes/deals/order-detail'), 'OrderDetailPage'),
  lazyRoute('/sourcing', () => import('~/routes/deals/sourcing'), 'SourcingPage'),
  lazyRoute('/sourcing/$id', () => import('~/routes/deals/sourcing-detail'), 'SourcingDetailPage'),
  lazyRoute(
    '/purchase-orders',
    () => import('~/routes/deals/purchase-orders'),
    'PurchaseOrdersPage',
  ),
  lazyRoute(
    '/purchase-orders/$id',
    () => import('~/routes/deals/purchase-orders'),
    'PurchaseOrderDetailPage',
  ),

  // Messaging
  lazyRoute('/messages', () => import('~/routes/chat/inbox'), 'InboxPage'),
  lazyRoute('/messages/$id', () => import('~/routes/chat/thread'), 'ThreadPage'),
])

const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
  scrollRestoration: true,
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

export function AppRouter() {
  return <RouterProvider router={router} />
}
